// src/components/StatementImportModal.jsx
// Smart bank / credit-card statement import modal with:
// - Auto-detection (Rabobank, HDFC, Credit Card)
// - Shift-click multi-select (Finder-style)
// - Bulk assignment (project, category, type)
// - Personal vs BRIO classification
// - PDF export of import summary
// - Auto-assignment memory: remembers counterparty → (type, category, project)

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Upload, Download } from 'lucide-react'; // eslint-disable-line no-unused-vars
import { BrioModal, BrioSelect, BrioTable, getTablePropsFromCode } from './BrioComponents';
import { parseStatement, FORMAT_LABELS } from '../utils/bankStatementParsers';
import { autoLinkBatch } from '../utils/expenseReceiptMatcher';
import { supabase } from '../supabaseClient';
import { OFFICE_OVERHEAD_CATEGORIES, PROJECT_EXPENSE_CATEGORIES, OFFICE_EXPENSE_ID } from '../utils/expenseShared';

const PRESENTATION_CODE = '1b';

// ── Auto-assignment rule helpers ─────────────────────────────────────────────
// Rules stored in Supabase app_settings (key 'statementAutoRules')
// Keyed by normalized counterparty or 'desc:' + normalized description
// → { assignedType, category, projectId }

function normalizeKey(str) {
  return (str || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function applyAutoRules(rows, rules) {
  if (!rules || Object.keys(rules).length === 0) return rows;
  return rows.map(r => {
    // Try counterparty match first, then fall back to description match
    const cpKey = normalizeKey(r.counterparty);
    const descKey = r.description ? 'desc:' + normalizeKey(r.description) : '';
    const rule = (cpKey && rules[cpKey]) || (descKey && rules[descKey]) || null;
    if (!rule) return r;
    return {
      ...r,
      assignedType: rule.assignedType || r.assignedType,
      category: rule.category || r.category,
      projectId: rule.projectId ?? r.projectId,
      _autoAssigned: true,
    };
  });
}

function buildUpdatedRules(existingRules, rows) {
  const rules = { ...(existingRules || {}) };
  rows.forEach(r => {
    // Only store if at least one field was explicitly assigned
    if (!r.assignedType && !r.category && !r.projectId) return;
    const ruleData = {
      assignedType: r.assignedType || 'brio',
      category: r.category || '',
      projectId: r.projectId ?? null,
    };
    // Learn by counterparty name
    const cpKey = normalizeKey(r.counterparty);
    if (cpKey) rules[cpKey] = ruleData;
    // Also learn by description (as fallback for entries without counterparty)
    const descKey = r.description ? 'desc:' + normalizeKey(r.description) : '';
    if (descKey) rules[descKey] = ruleData;
  });
  return rules;
}

// ── PDF export helper ──────────────────────────────────────────────────────

function generateImportPDF(rows, formatLabel, importDate) {
  const brioRows = rows.filter(r => r.assignedType === 'brio');
  const personalRows = rows.filter(r => r.assignedType === 'personal');

  const brioTotal = brioRows.reduce((s, r) => s + r.amount, 0);
  const personalTotal = personalRows.reduce((s, r) => s + r.amount, 0);
  const currency = rows[0]?.currency || 'EUR';
  const sym = currency === 'EUR' ? '€' : '₹';

  const fmtAmt = (v) => {
    if (currency === 'EUR') return sym + v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return sym + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderTable = (title, items, total) => {
    if (!items.length) return `<p style="color:#888;margin:8px 0;">No ${title.toLowerCase()} transactions.</p>`;
    return `
      <h3 style="font-size:13px;margin:16px 0 6px;color:#1a1a1a;border-bottom:1px solid #d4d0c8;padding-bottom:4px;">${title} (${items.length} items — ${fmtAmt(total)})</h3>
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr style="background:#f5f3ee;text-align:left;">
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Date</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Description</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Counterparty</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;text-align:right;">Amount</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Category</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Project</th>
          <th style="padding:4px 6px;border:1px solid #d4d0c8;">Receipt</th>
        </tr></thead>
        <tbody>${items.map(r => `
          <tr>
            <td style="padding:3px 6px;border:1px solid #e5e3de;">${r.date}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(r.description)}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;">${escapeHtml(r.counterparty)}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;text-align:right;${r.isDebit ? 'color:#c44;' : 'color:#2a7;'}">${r.isDebit ? '-' : '+'}${fmtAmt(r.amount)}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;">${escapeHtml(r.category || '—')}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;">${escapeHtml(r._projectName || '—')}</td>
            <td style="padding:3px 6px;border:1px solid #e5e3de;color:${r._receiptStatus === 'linked' ? '#2a7' : r._receiptStatus === 'not_required' ? '#888' : '#c44'};">${r._receiptStatus === 'linked' ? '✓ Yes' : r._receiptStatus === 'not_required' ? 'N/A' : '✗ Missing'}</td>
          </tr>`).join('')}
        </tbody>
      </table>`;
  };

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Statement Import — ${importDate}</title>
    <style>@page{size:A4 landscape;margin:15mm;}body{font-family:'Raleway',Helvetica,Arial,sans-serif;color:#1a1a1a;font-size:12px;}</style>
  </head><body>
    <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #9F902A;padding-bottom:8px;margin-bottom:12px;">
      <div><h1 style="font-size:16px;margin:0;color:#9F902A;">Statement Import Summary</h1>
        <p style="margin:2px 0 0;font-size:11px;color:#888;">Format: ${formatLabel} &bull; Imported: ${importDate}</p></div>
      <div style="text-align:right;font-size:11px;color:#888;">Architecture BRIO</div>
    </div>
    <div style="display:flex;gap:24px;margin-bottom:12px;">
      <div style="padding:8px 14px;background:#f5f3ee;flex:1;"><strong>Total transactions:</strong> ${rows.length}</div>
      <div style="padding:8px 14px;background:#e8f5e9;flex:1;"><strong>BRIO:</strong> ${brioRows.length} — ${fmtAmt(brioTotal)}</div>
      <div style="padding:8px 14px;background:#fff3e0;flex:1;"><strong>Personal:</strong> ${personalRows.length} — ${fmtAmt(personalTotal)}</div>
    </div>
    ${renderTable('BRIO Transactions', brioRows, brioTotal)}
    ${renderTable('Personal Transactions', personalRows, personalTotal)}
    <p style="font-size:9px;color:#aaa;margin-top:24px;text-align:center;">Generated by TimeTracker Pro — ${new Date().toISOString()}</p>
  </body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `statement-import-${importDate}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── Main component ─────────────────────────────────────────────────────────

export default function StatementImportModal({
  onClose,
  activeProjects = [],
  teamMembers = [],
  supabaseExpenses,
  reimbursements = [],
  formatMoney, // eslint-disable-line no-unused-vars
  displayCurrency = 'INR',
  getFxRate,
  autoRules = {},
  updateSetting,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // ── Step state ──
  const [step, setStep] = useState('paste'); // 'paste' | 'preview'
  const [csvText, setCsvText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [detectedFormat, setDetectedFormat] = useState('');
  const [detectedFormatLabel, setDetectedFormatLabel] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [autoAssignedCount, setAutoAssignedCount] = useState(0);
  const [importCurrency, setImportCurrency] = useState(''); // '' = auto-detect from parser
  const [importUserId, setImportUserId] = useState('');   // '' = unassigned

  // ── Selection state (Finder-style shift-select) ──
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const lastClickedIdx = useRef(null);

  // ── Bulk assignment state ──
  const [bulkProject, setBulkProject] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkType, setBulkType] = useState(''); // 'brio' | 'personal'

  // ── Sort ──
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  const sortedRows = useMemo(() => {
    const sorted = [...parsedRows];
    sorted.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'amount') { va = a.amountSigned; vb = b.amountSigned; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [parsedRows, sortKey, sortDir]);

  // Map for quick index lookup by _lineNum
  const lineNumToIdx = useMemo(() => {
    const map = {};
    parsedRows.forEach((r, i) => { map[r._lineNum] = i; });
    return map;
  }, [parsedRows]);

  // ── Parse ──
  const handlePreview = useCallback(() => {
    if (!csvText.trim()) return;
    const result = parseStatement(csvText);
    const enriched = applyAutoRules(result.rows, autoRules);
    // Apply global currency override if user selected one
    const effectiveCurrency = importCurrency || null;
    if (effectiveCurrency) enriched.forEach(r => { r.currency = effectiveCurrency; });
    const autoCount = enriched.filter(r => r._autoAssigned).length;
    setParsedRows(enriched);
    setAutoAssignedCount(autoCount);
    // With no rows the modal stays on the paste step, so say why there
    setParseErrors(enriched.length === 0 && result.errors.length === 0
      ? [`No transactions found in this ${result.formatLabel} statement.`]
      : result.errors);
    setDetectedFormat(result.format);
    setDetectedFormatLabel(result.formatLabel);
    setSelectedIndices(new Set());
    lastClickedIdx.current = null;
    if (enriched.length > 0) setStep('preview');
  }, [csvText, importCurrency]);

  const handleCurrencyChange = useCallback((newCur) => {
    setImportCurrency(newCur);
    if (newCur) setParsedRows(prev => prev.map(r => ({ ...r, currency: newCur })));
  }, []);

  // ── Row click with shift-select (Finder-style) ──
  const handleRowClick = useCallback((idx, e) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);

      if (e.shiftKey && lastClickedIdx.current !== null) {
        // Range select
        const start = Math.min(lastClickedIdx.current, idx);
        const end = Math.max(lastClickedIdx.current, idx);
        for (let i = start; i <= end; i++) next.add(i);
      } else if (e.metaKey || e.ctrlKey) {
        // Toggle single
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
      } else {
        // Single select (clear others)
        if (next.size === 1 && next.has(idx)) {
          next.delete(idx);
        } else {
          next.clear();
          next.add(idx);
        }
      }
      return next;
    });
    lastClickedIdx.current = idx;
  }, []);

  // ── Select all / none ──
  const handleSelectAll = useCallback(() => {
    if (selectedIndices.size === sortedRows.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(sortedRows.map((_, i) => i)));
    }
  }, [selectedIndices.size, sortedRows]);

  // ── Bulk apply ──
  const applyBulk = useCallback((field, value) => {
    if (selectedIndices.size === 0) return;
    // Map sorted indices back to parsedRows indices
    const sortedLineNums = new Set();
    selectedIndices.forEach(si => {
      if (sortedRows[si]) sortedLineNums.add(sortedRows[si]._lineNum);
    });

    setParsedRows(prev => prev.map(r => {
      if (!sortedLineNums.has(r._lineNum)) return r;
      if (field === 'projectId') return { ...r, projectId: value || null };
      if (field === 'category') return { ...r, category: value };
      if (field === 'assignedType') return { ...r, assignedType: value };
      return r;
    }));
  }, [selectedIndices, sortedRows]);

  // ── Single row field change ──
  const handleRowField = useCallback((lineNum, field, value) => {
    setParsedRows(prev => prev.map(r => {
      if (r._lineNum !== lineNum) return r;
      return { ...r, [field]: field === 'projectId' ? (value || null) : value };
    }));
  }, []);

  // ── Remove personal rows from import ──
  const personalCount = parsedRows.filter(r => r.assignedType === 'personal').length;
  const brioCount = parsedRows.filter(r => r.assignedType === 'brio').length;

  // ── Project name enrichment for PDF ──
  const projectMap = useMemo(() => {
    const m = {};
    activeProjects.forEach(p => { m[p.id] = p.name || p.projectCode || `#${p.id}`; });
    return m;
  }, [activeProjects]);

  // ── Import ──
  const handleImport = useCallback(async () => {
    const brioRows = parsedRows.filter(r => r.assignedType === 'brio');
    const personalRows = parsedRows.filter(r => r.assignedType === 'personal');
    if (brioRows.length === 0) {
      alert('No BRIO transactions to import. Mark at least one transaction as BRIO.');
      return;
    }

    setImporting(true);
    const batchId = `stmt_${Date.now()}`;
    const importSource = `bank-import-${detectedFormat}`;
    try {
      // Split BRIO rows into project expenses vs office overheads
      const projectBrioRows = brioRows.filter(r => r.projectId && r.projectId !== OFFICE_EXPENSE_ID);
      const officeBrioRows  = brioRows.filter(r => !r.projectId || r.projectId === OFFICE_EXPENSE_ID);

      // Map project rows → expenses table format
      const projectExpenseRows = projectBrioRows.map(r => {
        const cur = r.currency || 'INR';
        let fxRate = 1;
        let amtInr = r.amount;
        if (cur === 'EUR' && getFxRate) {
          fxRate = getFxRate(r.date) || 1;
          amtInr = fxRate > 1 ? Math.round(r.amount * fxRate * 100) / 100 : r.amount;
        }
        return {
          projectId: Number(r.projectId),
          // Uncategorised project rows default to a canonical project category —
          // never the non-canonical 'Bank Payment', which orphans the row.
          category: r.category || 'Miscellaneous',
          description: r.description,
          amount: amtInr,
          date: r.date,
          status: r.status || 'planned',
          officeLocation: r.officeLocation,
          amountOriginal: r.amount,
          currencyOriginal: cur,
          fxRateToInr: fxRate,
          amountInInr: amtInr,
          importSource,
          importBatchId: batchId,
        };
      });

      // Map office rows → expenses table format (is_office_expense = true)
      // expenses is now the single source of truth for all bank-imported transactions.
      const officeExpenseRows = officeBrioRows.map(r => {
        const cur = r.currency || 'INR';
        let fxRate = 1;
        let amtInr = r.amount;
        if (cur === 'EUR' && getFxRate) {
          fxRate = getFxRate(r.date) || 1;
          amtInr = fxRate > 1 ? Math.round(r.amount * fxRate * 100) / 100 : r.amount;
        }
        return {
          projectId:        null,
          // Uncategorised office rows default to the canonical 'Finance' bucket —
          // 'Bank Payment' is not a canonical category and would be hidden/flagged.
          category:         r.category || 'Finance',
          description:      r.description || r.counterparty || 'Office Expense',
          amount:           amtInr,
          date:             r.date || new Date().toISOString().split('T')[0],
          status:           'planned',
          officeLocation:   r.officeLocation || 'NL',
          amountOriginal:   r.amount,
          currencyOriginal: cur,
          fxRateToInr:      fxRate,
          amountInInr:      amtInr,
          importSource,
          importBatchId:    batchId,
          isOfficeExpense:  true,
        };
      });

      // Import project expenses → expenses table
      let result = { successCount: 0, errors: [] };
      if (projectExpenseRows.length > 0 && supabaseExpenses?.bulkImportExpenses) {
        result = await supabaseExpenses.bulkImportExpenses(projectExpenseRows);
      }

      // Import office expenses → expenses table (is_office_expense = true)
      if (officeExpenseRows.length > 0 && supabaseExpenses?.bulkImportExpenses) {
        const officeResult = await supabaseExpenses.bulkImportExpenses(officeExpenseRows);
        result.successCount += officeResult.successCount;
        result.errors.push(...(officeResult.errors || []));
      }

      // Learn auto-assignment rules from all assigned rows → persist to Supabase
      if (updateSetting) {
        const updated = buildUpdatedRules(autoRules, parsedRows);
        updateSetting('statementAutoRules', updated);
      }

      // Write audit record to statement_imports
      const brioTotal = brioRows.reduce((s, r) => s + r.amount, 0);
      const persTotal = personalRows.reduce((s, r) => s + r.amount, 0);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await supabase.from('statement_imports').insert([{
          batch_id:        batchId,
          format:          detectedFormat,
          format_label:    detectedFormatLabel,
          office_location: brioRows[0]?.officeLocation || 'NL',
          currency:        brioRows[0]?.currency || 'EUR',
          total_rows:      parsedRows.length,
          brio_rows:       brioRows.length,
          personal_rows:   personalRows.length,
          imported_rows:   result.successCount,
          error_count:     result.errors?.length || 0,
          brio_total:      Math.round(brioTotal * 100) / 100,
          personal_total:  Math.round(persTotal * 100) / 100,
          imported_by:     session?.user?.id || null,
        }]);
      } catch (auditErr) {
        console.warn('[StatementImport] Audit record failed:', auditErr.message);
      }

      // Auto-link imported expenses to existing receipts
      let autoLinkedCount = 0;
      if (result.successCount > 0 && reimbursements.length > 0 && supabaseExpenses?.linkExpenseToReimbursement) {
        try {
          // Re-fetch the just-imported expenses to get their IDs
          const { data: importedExpenses } = await supabase
            .from('expenses')
            .select('*')
            .eq('import_batch_id', batchId);

          if (importedExpenses?.length) {
            const mapped = importedExpenses.map(e => ({
              id: e.id, amount: e.amount, amountInInr: e.amount_in_inr, date: e.date,
              description: e.description, category: e.category, currencyOriginal: e.currency_original,
            }));
            const links = autoLinkBatch(mapped, reimbursements);
            for (const link of links) {
              const res = await supabaseExpenses.linkExpenseToReimbursement(link.expenseId, link.reimbursementId);
              if (res.success) autoLinkedCount++;
            }
            // Update audit record with receipt counts
            if (links.length > 0) {
              await supabase.from('statement_imports')
                .update({
                  receipt_linked_count: autoLinkedCount,
                  receipt_missing_count: result.successCount - autoLinkedCount,
                  updated_at: new Date().toISOString(),
                })
                .eq('batch_id', batchId);
            }
          }
        } catch (linkErr) {
          console.warn('[StatementImport] Auto-link failed:', linkErr.message);
        }
      }

      setImportResult({
        imported: result.successCount,
        total: brioRows.length,
        personal: personalCount,
        errors: result.errors || [],
        batchId,
        autoLinked: autoLinkedCount,
      });
    } catch (err) {
      setImportResult({ imported: 0, total: brioRows.length, personal: personalCount, errors: [err.message] });
    } finally {
      setImporting(false);
    }
  }, [parsedRows, personalCount, supabaseExpenses, getFxRate, reimbursements, autoRules, updateSetting]);

  // ── PDF export ──
  const handleExportPDF = useCallback(() => {
    const enriched = parsedRows.map(r => ({
      ...r,
      _projectName: r.projectId === OFFICE_EXPENSE_ID ? 'Office Expense' : (r.projectId ? (projectMap[r.projectId] || '') : ''),
    }));
    generateImportPDF(enriched, detectedFormatLabel, new Date().toISOString().split('T')[0]);
  }, [parsedRows, projectMap, detectedFormatLabel]);

  // ── Sort click handler ──
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortIcon = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ── Dropdown projects ──
  const dropdownProjects = useMemo(
    () => activeProjects.filter(p => p.status === 'active' || p.status === 'paused'),
    [activeProjects]
  );

  const fmtAmt = (v, cur) => {
    const s = cur === 'EUR' ? '€' : '₹';
    return `${s}${Math.abs(v).toLocaleString(cur === 'EUR' ? 'nl-NL' : 'en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Render ───────────────────────────────────────────────────────────────

  // Step 1: Paste
  if (step === 'paste') {
    return (
      <BrioModal title="Import Bank / CC Statement" onClose={onClose} width="lg"
        footer={
          <div className="brio-flex brio-justify-end brio-gap-xs">
            <button className="brio-btn brio-btn-secondary" onClick={onClose}>Cancel</button>
            <button className="brio-btn brio-btn-primary" onClick={handlePreview} disabled={!csvText.trim()}>
              Preview
            </button>
          </div>
        }>
        <div style={{ padding: '0' }}>
          <p className="brio-analytics-muted" style={{ fontSize: 11, marginBottom: 8 }}>
            Paste your bank or credit card statement CSV below. The format will be auto-detected.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <p className="brio-analytics-muted" style={{ fontSize: 10, margin: 0, color: '#888' }}>
              Supported: {Object.values(FORMAT_LABELS).filter(l => l !== 'Unknown Format').join(', ')}
            </p>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>Assign to:</label>
                <select value={importUserId} onChange={e => setImportUserId(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 6px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}>
                  <option value="">— Person —</option>
                  {teamMembers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>Statement currency:</label>
                <select value={importCurrency} onChange={e => setImportCurrency(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 6px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}>
                  <option value="">Auto-detect</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="INR">INR (₹)</option>
                </select>
              </div>
            </div>
          </div>
          <textarea
            value={csvText}
            onChange={e => { setCsvText(e.target.value); setParseErrors([]); }}
            placeholder="Paste CSV data here..."
            style={{
              width: '100%', minHeight: 220, fontFamily: 'monospace', fontSize: 11,
              padding: 10, border: '1px solid var(--brio-border, #d4d0c8)',
              borderRadius: 0, resize: 'vertical', background: '#fafaf8',
            }}
          />
          {parseErrors.length > 0 && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '6px 10px', marginTop: 8, fontSize: 11 }}>
              {parseErrors.slice(0, 3).join('; ')}
              {parseErrors.length > 3 && ` ...and ${parseErrors.length - 3} more`}
            </div>
          )}
        </div>
      </BrioModal>
    );
  }

  // Step 2: Preview & assign
  if (step === 'preview' && !importResult) {
    const brioTotal = parsedRows.filter(r => r.assignedType === 'brio').reduce((s, r) => s + r.amountSigned, 0);
    const personalTotal = parsedRows.filter(r => r.assignedType === 'personal').reduce((s, r) => s + r.amountSigned, 0);
    const mainCurrency = parsedRows[0]?.currency || 'EUR';

    return (
      <BrioModal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>
              Import Statement
              <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 8, color: 'var(--brio-gold, #9F902A)' }}>
                Detected: {detectedFormatLabel}
              </span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 400, color: '#666' }}>Assign to:</span>
              <select value={importUserId} onChange={e => setImportUserId(e.target.value)}
                style={{ fontSize: 10, padding: '1px 5px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3, fontWeight: 400 }}>
                <option value="">— Person —</option>
                {teamMembers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 400, color: '#666' }}>Currency:</span>
              <select value={importCurrency || parsedRows[0]?.currency || 'EUR'}
                onChange={e => handleCurrencyChange(e.target.value)}
                style={{ fontSize: 10, padding: '1px 5px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3, fontWeight: 400 }}>
                <option value="EUR">EUR (€)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </span>
          </span>
        }
        onClose={onClose} width="fullwidth"
        className="brio-modal--full-height"
        footer={
          <div className="brio-flex brio-justify-between brio-items-center" style={{ width: '100%' }}>
            <div className="brio-flex brio-gap-xs">
              <button className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                onClick={() => { setStep('paste'); setImportResult(null); }}>
                ← Back
              </button>
              <button className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" onClick={handleExportPDF}>
                <Download className="w-3 h-3" /> Export Summary
              </button>
            </div>
            <div className="brio-flex brio-gap-xs brio-items-center">
              <span style={{ fontSize: 10, color: '#888' }}>
                {brioCount} BRIO &bull; {personalCount} personal &bull; {parsedRows.length} total
              </span>
              <button className="brio-btn brio-btn-primary" onClick={handleImport} disabled={importing || brioCount === 0}>
                {importing ? 'Importing...' : `Import ${brioCount} BRIO Transactions`}
              </button>
            </div>
          </div>
        }>

        {/* ── Errors ── */}
        {parseErrors.length > 0 && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '6px 10px', marginBottom: 8, fontSize: 11 }}>
            {parseErrors.length} parse warning(s): {parseErrors.slice(0, 3).join('; ')}
            {parseErrors.length > 3 && ` ...and ${parseErrors.length - 3} more`}
          </div>
        )}

        {/* ── Auto-assigned banner ── */}
        {autoAssignedCount > 0 && (
          <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', padding: '5px 10px', marginBottom: 8, fontSize: 11 }}>
            <span style={{ color: '#1565c0' }}>&#9679;</span>{' '}
            <strong>{autoAssignedCount}</strong> of {parsedRows.length} rows auto-assigned from previous imports.
            You can override any assignment below.
          </div>
        )}

        {/* ── Summary band ── */}
        <div className="brio-flex brio-gap-s" style={{ marginBottom: 10, fontSize: 11 }}>
          <div style={{ padding: '6px 12px', background: '#f5f3ee', flex: 1 }}>
            <strong>Total:</strong> {parsedRows.length} transactions
          </div>
          <div style={{ padding: '6px 12px', background: '#e8f5e9', flex: 1 }}>
            <strong>BRIO:</strong> {brioCount} — {fmtAmt(brioTotal, mainCurrency)}
          </div>
          <div style={{ padding: '6px 12px', background: '#fff3e0', flex: 1 }}>
            <strong>Personal:</strong> {personalCount} — {fmtAmt(personalTotal, mainCurrency)}
          </div>
        </div>

        {/* ── Bulk actions bar ── */}
        {selectedIndices.size > 0 && (
          <div className="brio-flex brio-items-center brio-gap-xs" style={{
            background: '#f5f3ee', padding: '6px 10px', marginBottom: 8, fontSize: 11,
            border: '1px solid var(--brio-border, #d4d0c8)',
          }}>
            <span style={{ fontWeight: 600, marginRight: 4 }}>
              {selectedIndices.size} selected:
            </span>

            <BrioSelect
              value={bulkType}
              onChange={e => { setBulkType(e.target.value); applyBulk('assignedType', e.target.value); }}
              style={{ padding: '2px 6px', fontSize: 10, minWidth: 90 }}>
              <option value="">— Type —</option>
              <option value="brio">BRIO</option>
              <option value="personal">Personal</option>
            </BrioSelect>

            <BrioSelect
              value={bulkProject}
              onChange={e => {
                const v = e.target.value;
                setBulkProject(v);
                applyBulk('projectId', v === OFFICE_EXPENSE_ID ? OFFICE_EXPENSE_ID : (v ? Number(v) : null));
              }}
              style={{ padding: '2px 6px', fontSize: 10, minWidth: 120 }}>
              <option value="">— Project —</option>
              <option value={OFFICE_EXPENSE_ID}>Office Expense</option>
              <option disabled>──────────</option>
              {dropdownProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name || p.projectCode}</option>
              ))}
            </BrioSelect>

            <BrioSelect
              value={bulkCategory}
              onChange={e => { setBulkCategory(e.target.value); applyBulk('category', e.target.value); }}
              style={{ padding: '2px 6px', fontSize: 10, minWidth: 110 }}>
              <option value="">— Category —</option>
              <optgroup label="Office">
                {OFFICE_OVERHEAD_CATEGORIES.map(c => <option key={`o-${c}`} value={c}>{c}</option>)}
              </optgroup>
              <optgroup label="Project">
                {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={`p-${c}`} value={c}>{c}</option>)}
              </optgroup>
            </BrioSelect>

            <button className="brio-btn brio-btn-secondary" style={{ padding: '2px 8px', fontSize: 10 }}
              onClick={() => setSelectedIndices(new Set())}>
              Clear Selection
            </button>
          </div>
        )}

        {/* ── Hint ── */}
        <p style={{ fontSize: 9, color: '#999', marginBottom: 6 }}>
          Click to select. Shift+click for range. Cmd/Ctrl+click to toggle. Use bulk actions above to assign multiple rows.
        </p>

        {/* ── Table ── */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '2%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '27%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ padding: '4px', textAlign: 'center' }}>
                  <input type="checkbox" checked={selectedIndices.size === sortedRows.length && sortedRows.length > 0}
                    onChange={handleSelectAll} title="Select all" />
                </th>
                <th style={{ cursor: 'pointer', padding: '4px 6px' }} onClick={() => handleSort('date')}>Date{sortIcon('date')}</th>
                <th style={{ cursor: 'pointer', padding: '4px 6px' }} onClick={() => handleSort('description')}>Description{sortIcon('description')}</th>
                <th style={{ cursor: 'pointer', padding: '4px 6px' }} onClick={() => handleSort('counterparty')}>Counterparty{sortIcon('counterparty')}</th>
                <th style={{ cursor: 'pointer', padding: '4px 6px', textAlign: 'right' }} onClick={() => handleSort('amount')}>Amount{sortIcon('amount')}</th>
                <th style={{ padding: '4px 6px' }}>Type</th>
                <th style={{ padding: '4px 6px' }}>Project</th>
                <th style={{ padding: '4px 6px' }}>Category</th>
                <th style={{ padding: '4px 6px' }}>Ref</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, idx) => {
                const isSelected = selectedIndices.has(idx);
                const isPersonal = row.assignedType === 'personal';
                return (
                  <tr key={row._lineNum}
                    onClick={(e) => handleRowClick(idx, e)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? '#e8eaf6' : (isPersonal ? '#fff8e1' : undefined),
                      opacity: isPersonal ? 0.7 : 1,
                      userSelect: 'none',
                    }}>
                    <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                      <input type="checkbox" checked={isSelected} readOnly tabIndex={-1}
                        style={{ pointerEvents: 'none' }} />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: 11, whiteSpace: 'nowrap' }}>{row.date}</td>
                    <td style={{ padding: '3px 6px', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={row.description}>
                      {row.description}
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={row.counterparty}>
                      {row._autoAssigned && <span style={{ color: '#1565c0', marginRight: 3, fontSize: 8 }}>&#9679;</span>}
                      {row.counterparty}
                    </td>
                    <td style={{
                      padding: '3px 6px', fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      color: row.isDebit ? '#c44' : '#2a7', fontWeight: 500,
                    }}>
                      {row.isDebit ? '-' : '+'}{fmtAmt(row.amount, row.currency)}
                    </td>
                    <td style={{ padding: '3px 4px' }}>
                      <select
                        value={row.assignedType}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); handleRowField(row._lineNum, 'assignedType', e.target.value); }}
                        style={{
                          fontSize: 9, padding: '1px 2px', width: '100%', border: '1px solid #d4d0c8',
                          background: row.assignedType === 'personal' ? '#fff3e0' : '#e8f5e9',
                          fontFamily: 'var(--brio-font-family)',
                        }}>
                        <option value="brio">BRIO</option>
                        <option value="personal">Personal</option>
                      </select>
                    </td>
                    <td style={{ padding: '3px 4px' }}>
                      <select
                        value={row.projectId || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          e.stopPropagation();
                          const v = e.target.value;
                          handleRowField(row._lineNum, 'projectId', v === OFFICE_EXPENSE_ID ? OFFICE_EXPENSE_ID : (v ? Number(v) : null));
                        }}
                        style={{ fontSize: 9, padding: '1px 2px', width: '100%', border: '1px solid #d4d0c8', fontFamily: 'var(--brio-font-family)' }}>
                        <option value="">—</option>
                        <option value={OFFICE_EXPENSE_ID}>Office Expense</option>
                        <option disabled>──────────</option>
                        {dropdownProjects.map(p => (
                          <option key={p.id} value={p.id}>{p.name || p.projectCode}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '3px 4px' }}>
                      <select
                        value={row.category}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); handleRowField(row._lineNum, 'category', e.target.value); }}
                        style={{ fontSize: 9, padding: '1px 2px', width: '100%', border: '1px solid #d4d0c8', fontFamily: 'var(--brio-font-family)' }}>
                        <option value="">—</option>
                        {(row.projectId === OFFICE_EXPENSE_ID || !row.projectId
                          ? OFFICE_OVERHEAD_CATEGORIES
                          : PROJECT_EXPENSE_CATEGORIES
                        ).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: 9, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={row.reference}>
                      {row.reference}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </BrioTable>
        </div>
      </BrioModal>
    );
  }

  // Step 3: Import result
  if (importResult) {
    return (
      <BrioModal title="Import Complete" onClose={onClose} width="md"
        footer={
          <div className="brio-flex brio-justify-between" style={{ width: '100%' }}>
            <button className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" onClick={handleExportPDF}>
              <Download className="w-3 h-3" /> Export Summary
            </button>
            <button className="brio-btn brio-btn-primary" onClick={onClose}>Done</button>
          </div>
        }>
        <div style={{ fontSize: 12 }}>
          <div style={{ padding: '12px', background: importResult.errors.length ? '#fff3cd' : '#e8f5e9', marginBottom: 12 }}>
            <strong>{importResult.imported}</strong> of {importResult.total} BRIO transactions imported successfully.
            {importResult.personal > 0 && (
              <span style={{ marginLeft: 8 }}><strong>{importResult.personal}</strong> personal transactions excluded.</span>
            )}
            {importResult.autoLinked > 0 && (
              <div style={{ marginTop: 6, color: '#1565c0' }}>
                <strong>{importResult.autoLinked}</strong> transactions auto-linked to uploaded receipts.
              </div>
            )}
            {importResult.reimbursementCount > 0 && (
              <div style={{ marginTop: 6, color: '#2e7d32' }}>
                <strong>{importResult.reimbursementCount}</strong> records added to Payouts &amp; Vouchers (pre-approved).
              </div>
            )}
          </div>
          {importResult.errors.length > 0 && (
            <div style={{ fontSize: 11, color: '#c44' }}>
              <strong>Errors:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      </BrioModal>
    );
  }

  return null;
}
